import React, { useState, useEffect, useMemo } from "react"
import Modal from "aldoo-ra/Modal"
import Typography from "aldoo-ra/Typography"
import Button from "aldoo-ra/Button"
import DataTable from "aldoo-ra/DataTable"
import Checkbox from "aldoo-ra/Checkbox"
import { showNotification } from "aldoo-ra/Notification"
import { showPromptDialog } from "aldoo-ra/PromptDialog"
import { GetForwardRules } from "./forward-rules-api"
import { TrashIcon, XMarkIcon } from "@heroicons/react/24/outline"
import { api, generateCRUDInterface } from "aldoo-ra/API"

const crudAPI = api("crud")
const ForwardRuleCRUD = generateCRUDInterface(crudAPI, "CSForwardRule")

export default function RestoreForwardRulesDialog({
  isOpen,
  onClose,
  parentRefreshKey,
  onRulesRestored,
}) {
  const [selectedItems, setSelectedItems] = useState([])
  const [refreshKey, setRefreshKey] = useState(0)
  const [searchText, setSearchText] = useState("")
  const [dataSource, setDataSource] = useState([])
  const [itemsPerPage, setItemsPerPage] = useState(10)

  // Reset selections when dialog opens
  useEffect(() => {
    if (isOpen) {
      setSelectedItems([])
      setSearchText("")
      setRefreshKey((prev) => prev + 1)
    }
  }, [isOpen])

  // Refresh when parent's refresh key changes
  useEffect(() => {
    if (isOpen && parentRefreshKey) {
      setRefreshKey((prev) => prev + 1)
    }
  }, [parentRefreshKey, isOpen])

  // Handle restore action
  const handleRestore = async () => {
    if (selectedItems.length === 0) return

    try {
      const promises = selectedItems.map((id) =>
        ForwardRuleCRUD.Update({
          _id: id,
          deleted: false,
        })
      )

      const results = await Promise.all(promises)
      const successCount = results.filter((r) => r.result || r).length

      if (successCount > 0) {
        showNotification({
          message: `${successCount} ${
            successCount === 1 ? "rule" : "rules"
          } restored successfully`,
          className: "bg-admin_primary text-admin_text_inv",
        })

        setSelectedItems([])
        setRefreshKey((prev) => prev + 1)

        if (onRulesRestored) {
          onRulesRestored()
        }
      } else {
        showNotification({
          message: "Failed to restore rules",
          className: "bg-admin_error text-white",
        })
      }
    } catch (error) {
      showNotification({
        message: "An error occurred while restoring rules",
        className: "bg-admin_error text-white",
      })
    }
  }

  // Handle permanent delete action
  const handleDeleteForever = async () => {
    if (selectedItems.length === 0) return

    showPromptDialog({
      title: "Permanently Delete Rules",
      message: `Are you sure you want to permanently delete ${selectedItems.length} ${
        selectedItems.length === 1 ? "rule" : "rules"
      }? This action cannot be undone.`,
      confirmText: "Delete Forever",
      confirmVariant: "error_m",
      cancelText: "Cancel",
      onConfirm: async () => {
        try {
          const promises = selectedItems.map((id) =>
            ForwardRuleCRUD.HardDelete({ _id: id })
          )

          const results = await Promise.all(promises)
          const successCount = results.filter((r) => r.result || r).length

          if (successCount > 0) {
            showNotification({
              message: `${successCount} ${
                successCount === 1 ? "rule" : "rules"
              } permanently deleted`,
              className: "bg-admin_primary text-admin_text_inv",
            })

            setSelectedItems([])
            setRefreshKey((prev) => prev + 1)

            if (onRulesRestored) {
              onRulesRestored()
            }
          } else {
            showNotification({
              message: "Failed to permanently delete rules",
              className: "bg-admin_error text-white",
            })
          }
        } catch (error) {
          showNotification({
            message: "An error occurred while permanently deleting rules",
            className: "bg-admin_error text-white",
          })
        }
      },
    })
  }

  // Handle select all change
  const handleSelectAll = (items, isSelected) => {
    if (isSelected) {
      setSelectedItems(items.map((item) => item._id))
    } else {
      setSelectedItems([])
    }
  }

  // Handle individual selection change
  const handleSelectItem = (itemId, isSelected) => {
    if (isSelected) {
      setSelectedItems((prev) => [...prev, itemId])
    } else {
      setSelectedItems((prev) => prev.filter((id) => id !== itemId))
    }
  }

  // Handle search value from the DataTable's internal search
  const handleSearchChange = (value) => {
    setSearchText(value)
  }

  // Handle items per page change
  const handleItemsPerPageChange = (newItemsPerPage) => {
    setItemsPerPage(newItemsPerPage)
  }

  // Handle data loaded
  const handleDataLoaded = (loadedData) => {
    setDataSource(loadedData || [])
    return loadedData
  }

  // Determine if all visible items are selected
  const allVisibleSelected = useMemo(() => {
    const currentVisibleItems = dataSource.slice(0, itemsPerPage)
    return (
      currentVisibleItems.length > 0 &&
      currentVisibleItems.every((item) => selectedItems.includes(item._id))
    )
  }, [dataSource, selectedItems, itemsPerPage])

  // Style overrides for the DataTable
  const styleOverrides = {
    searchInput: "w-full max-w-[300px] ml-auto",
    getRowStyle: (index, item) => {
      if (item && selectedItems.includes(item._id)) {
        return "bg-admin_primary/10 border-l-2 border-admin_primary"
      }
    },
  }

  // Get request fields - set match to only show deleted rules
  const getRequestFields = () => {
    return {
      match: { deleted: true },
    }
  }

  // Table columns
  const columns = [
    {
      id: "select",
      headerClassName: "w-[60px]",
      header: (value, items) => (
        <Checkbox
          variant="admin"
          isOn={items.length > 0 && selectedItems.length === items.length}
          onChange={(isSelected) => handleSelectAll(items, isSelected)}
        />
      ),
      render: (value, item) => (
        <Checkbox
          variant="admin"
          isOn={selectedItems.includes(item._id)}
          onChange={(isSelected) => handleSelectItem(item._id, isSelected)}
        />
      ),
    },
    {
      id: "subscriber",
      title: "Subscriber",
      headerClassName: "w-1/3",
      render: (value, item) => (
        <Typography variant="admin_body" className="my-0">
          {value}
        </Typography>
      ),
    },
    {
      id: "tags",
      title: "Tags",
      headerClassName: "w-2/3",
      render: (value, item) => (
        <div className="flex flex-wrap gap-2">
          {value && value.length > 0 ? (
            value.map((tag, index) => (
              <span
                key={index}
                className="px-3 py-1 bg-admin_primary/10 text-admin_primary rounded-full text-sm"
              >
                {typeof tag === "string" ? tag : tag.name || tag.value || tag}
              </span>
            ))
          ) : (
            <Typography variant="admin_body" className="text-admin_text_secondary my-0">
              No tags
            </Typography>
          )}
        </div>
      ),
    },
  ]

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      variant="admin"
      className="md:absolute w-full sm:w-[500px] md:w-[95%] lg:max-w-[1200px] h-full md:h-auto md:min-h-[85%]"
      closeOnEscape
      title="Restore Deleted Forward Rules"
    >
      <div className="p-6">
        <div className="min-h-[500px]">
          <DataTable
            apiMethod={GetForwardRules}
            variant="admin_browser"
            preferredHeight="70vh"
            itemHeight="h-[60px]"
            showFooter={true}
            searchField="subscriber tags"
            localUserPreferencesKey="admin.customerSupport.restoreForwardRules"
        renders={columns}
            refreshKey={refreshKey}
            sort={{ deletedAt: -1 }}
            styleOverrides={styleOverrides}
            requestFields={getRequestFields()}
            noData={
              <Typography variant="admin_body">
                No deleted forward rules found
              </Typography>
            }
            onSearchChange={handleSearchChange}
            onDataLoaded={handleDataLoaded}
            onItemsPerPageChange={handleItemsPerPageChange}
            headerControls={
              <div className="flex items-center">
                {selectedItems.length > 0 && (
                  <>
                    <Typography
                      variant="admin_txt1"
                      className="whitespace-nowrap mr-4"
                    >
                      {selectedItems.length}{" "}
                      {selectedItems.length === 1 ? "rule" : "rules"} selected
                    </Typography>

                    <Button
                      variant="admin_secondary"
                      onClick={() => setSelectedItems([])}
                      className="whitespace-nowrap mr-2 flex items-center"
                    >
                      <XMarkIcon className="h-5 w-5 mr-1" /> Deselect
                    </Button>

                    <Button
                      variant="admin_primary"
                      onClick={handleRestore}
                      disabled={selectedItems.length === 0}
                      className="whitespace-nowrap mr-2"
                    >
                      Restore Selected
                    </Button>

                    <Button
                      variant="admin_primary_red"
                      onClick={handleDeleteForever}
                      disabled={selectedItems.length === 0}
                      className="whitespace-nowrap flex items-center"
                    >
                      <TrashIcon className="h-5 w-5 mr-1" /> Delete Forever
                    </Button>
                  </>
                )}
              </div>
            }
          />
        </div>
      </div>
    </Modal>
  )
}
